// SPDX-FileCopyrightText: 2025 Bundesrepublik Deutschland, vertreten durch das BMI/ITZBund
//
// SPDX-License-Identifier: GPL-3.0-or-later

/*
 * This file is part of the package itzbund/gsb-places of the GSB 11 Project by ITZBund
 *
 * Copyright (C) 2025 Bundesrepublik Deutschland, vertreten durch das
 * BMI/ITZBund. Author: Thomas Maroschik
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import {LitElement, html} from 'lit';
import {
  AttributionControl, Event, FullscreenControl, Map, MapOptions, NavigationControl, ResourceType,
  StyleOptions, StyleSpecification, StyleSwapOptions, setWorkerUrl
} from 'maplibre-gl';
import styles from './gsb-map.css';
import {isMapboxURL, transformMapboxUrl} from 'maplibregl-mapbox-request-transformer';
import {updateLabelLanguageCoalesce} from '../utils/style-helper';
import {gsbMapContext} from "../contexts/gsb-map-context";
import {provide} from "@lit/context";
import {state} from "@lit/reactive-element/decorators.js";
import {createMapFocusOrderMaintainer} from "../utils/a11y-helper";
import {deLocale} from "../locale/de";

import workerUrl from 'maplibre-gl/dist/maplibre-gl-worker?worker&url';
setWorkerUrl((new URL('.' + workerUrl, import.meta.url)).toString());

class CustomMap extends Map {

  protected styleUrl?: string;

  // TODO: remove this backwards compatibility method once this https://github.com/visgl/deck.gl/issues/10501 is fixed in deck.gl
  // The internal but exposed map.transform property was made private in maplibre-gl v6
  get transform() {
    return this._camera?.transform;
  }

  setStyle(style: StyleSpecification | string | null, options?: StyleSwapOptions & StyleOptions): this {
    if (typeof style === 'string') {
      this.styleUrl = style;
    }
    this.fire(new Event('style.loading'));
    return super.setStyle(style, options);
  }

  getStyleUrl():string {
    return this.styleUrl;
  }

}

export class GsbMap extends LitElement {
  public accessToken = null; // Mapbox access token
  public initialMapOptions: MapOptions|any = {
    minZoom: 0,
    maxZoom: 20,
    pitch: 0,
    bearing: 0,
    attributionControl: false,

  };
  public initialControls = {
    noNavigation: false,
  };
  public loaded: boolean;
  public zoomOnScroll = false;
  public mapPadding = {
    top: 16, bottom: 16, left: 16, right: 16,
  };
  public fullscreenControl = false;
  public reducedMotion = false;

  @provide({context: gsbMapContext})
  @state()
  public map: Map;

  public fitBoundsMaxZoom: any;
  protected _focusOrderMaintainer: MutationObserver;
  protected _navigationControl: NavigationControl;

  static get is() {
    return 'gsb-map';
  }

  static get styles() {
    return styles;
  }

  render() {
    return html`
      <div id="map"></div>
      <slot></slot>
    `;
  }

  static get properties() {
    return {

      interactive: {
        type: Boolean,
      },

      fullscreenControl: {
        type: Boolean,
        attribute: 'fullscreen-control',
      },

      reducedMotion: {
        type: Boolean,
        attribute: 'reduced-motion',
      },

      /** Does nothing */
      noNavigation: {
        type: Boolean,
        attribute: 'no-navigation',
      },

      accessToken: {
        type: String,
        attribute: 'access-token',
      },

      center: {
        type: Array,
      },

      zoom: {
        type: Number,
      },

      maxZoom: {
        type: Number,
      },

      fitBoundsMaxZoom: {
        type: Number,
        attribute: 'fit-bounds-max-zoom',
      },

      minZoom: {
        type: Number,
      },

      pitch: {
        type: Number,
      },

      bearing: {
        type: Number,
      },

      mapStyle: {
        type: String,
        attribute: 'map-style',
      },

      loaded: {
        type: Boolean,
      },

      zoomOnScroll: {
        type: Boolean,
        attribute: 'zoom-on-scroll',
      },

      bounds: {
        type: Array,
        attribute: 'bounds',
      },

      mapPadding: {
        type: Object,
        attribute: 'map-padding',
      }
    };
  }


  set mapStyle(mapStyle) {
    // eslint-disable-next-line no-unused-expressions
    this.map && this.map.setStyle(mapStyle);
    this.initialMapOptions.style = mapStyle;
  }

  get mapStyle() {
    return this.map ? this.map.getStyle() : this.initialMapOptions.style;
  }

  set bounds(lngLatBoundsLike) {
    // eslint-disable-next-line no-unused-expressions
    this.map && this.map.fitBounds(lngLatBoundsLike, {
      maxZoom: this.fitBoundsMaxZoom || 22,
      animate: !this.reducedMotion,
    });
    this.initialMapOptions.bounds = lngLatBoundsLike;
  }

  get bounds() {
    return this.map ? this.map.getBounds() : this.initialMapOptions.bounds;
  }

  set center(lngLatLike) {
    // eslint-disable-next-line no-unused-expressions
    this.map && this.map.setCenter(lngLatLike);
    this.initialMapOptions.center = lngLatLike;
  }

  get center() {
    return this.map ? this.map.getCenter() : this.initialMapOptions.center;
  }

  set zoom(zoom) {
    this.map && this.map.setZoom(zoom);
    this.initialMapOptions.zoom = zoom;
  }

  get zoom() {
    return this.map ? this.map.getZoom() : this.initialMapOptions.zoom;
  }

  set pitch(pitch) {
    // eslint-disable-next-line no-unused-expressions
    this.map && this.map.setPitch(pitch);
    this.initialMapOptions.pitch = pitch;
  }

  get pitch() {
    return this.map ? this.map.getPitch() : this.initialMapOptions.pitch;
  }

  set bearing(bearing) {
    // eslint-disable-next-line no-unused-expressions
    this.map && this.map.setBearing(bearing);
    this.initialMapOptions.bearing = bearing;
  }

  get bearing() {
    return this.map ? this.map.getBearing() : this.initialMapOptions.bearing;
  }

  // eslint-disable-next-line consistent-return
  set noNavigation(noNavigation) {
    if (!this.map) {
      this.initialControls = {...this.initialControls, noNavigation: !!noNavigation};
    }
    if (!noNavigation) {
      // eslint-disable-next-line no-unused-expressions
      !this._navigationControl && (this._navigationControl = new NavigationControl());
      this.map.addControl(this._navigationControl);
    } else if (noNavigation && this._navigationControl) {
      this.map.removeControl(this._navigationControl);
    }
  }

  get noNavigation() {
    if (!this.map) {
      return this.initialControls.noNavigation;
    }
    return !!this._navigationControl;
  }

  set interactive(interactive) {
    this.initialMapOptions.interactive = !!interactive;
  }

  get interactive() {
    return this.initialMapOptions.interactive;
  }

  set maxZoom(maxZoom) {
    // eslint-disable-next-line no-unused-expressions
    this.map && this.map.setMaxZoom(maxZoom);
    this.initialMapOptions.maxZoom = maxZoom;
  }

  get maxZoom() {
    return this.map ? this.map.getMaxZoom() : this.initialMapOptions.maxZoom;
  }

  set minZoom(minZoom) {
    // eslint-disable-next-line no-unused-expressions
    this.map && this.map.setMinZoom(minZoom);
    this.initialMapOptions.minZoom = minZoom;
  }

  get minZoom() {
    return this.map ? this.map.getMinZoom() : this.initialMapOptions.minZoom;
  }

  get stylesCompatibleWithMapboxLanguage() {
    return this.initialMapOptions
      && this.initialMapOptions.style
      && this.initialMapOptions.style.startsWith('mapbox://');
  }

  connectedCallback() {
    super.connectedCallback();
    this.loaded = true;
  }

  updated(changedProperties) {
    if (changedProperties.has('loaded') && this.loaded && !this.map) {
      this.createMap();
    }
  }

  createMap() {
    const transformRequest = (url: string, resourceType: ResourceType) => {
      if (isMapboxURL(url)) {
        return transformMapboxUrl(url, resourceType, this.accessToken);
      }
      return {url};
    };

    const mapContainer = this.shadowRoot.querySelector('#map');
    this._focusOrderMaintainer = createMapFocusOrderMaintainer();
    this._focusOrderMaintainer.observe(mapContainer);
    this.map = new CustomMap({
      container: mapContainer,
      ...this.initialMapOptions,
      style: null,
      transformRequest,
      locale: document.documentElement.getAttribute('lang') === 'de' ? deLocale : {}
    });
    this.map.setStyle(this.initialMapOptions.style);

    if (this.fullscreenControl) {
      this.map.addControl(new FullscreenControl());
    }

    this.map.addControl(new AttributionControl({
      compact: true,
    }));

    Object.keys(this.initialControls).forEach((controlName) => {
      this[controlName] = this.initialControls[controlName];
    });

    this.zoomOnScroll ? this.map.scrollZoom.enable() : this.map.scrollZoom.disable();
    //this.map.setPadding(this.mapPadding);

    this.map.on('style.load', () => {
      const firstSymbolLayer = this.map.getStyle().layers.find((l) => l.type === 'symbol');
      this.map.setGlobalStateProperty('firstSymbolLayer', firstSymbolLayer?.id || '');
    });

    this.map.on('load', (e) => {
      if (this.stylesCompatibleWithMapboxLanguage) {
        const language = document.documentElement.lang.slice(0, 2) || 'de';
        this.map.getStyle().layers
          .map(layer => layer.id)
          .filter(layerId => /label$/.test(layerId))
          .forEach((layerId) => {
            try {
              const currentLayoutProperty = this.map.getLayoutProperty(layerId, 'text-field');
              this.map.setLayoutProperty(
                layerId,
                'text-field',
                updateLabelLanguageCoalesce(currentLayoutProperty, language)
              );

            } catch (e) {
              console.warn(e);
            }
          });
      }
      // Move the navigation controls into the top container
      const navElement = this.shadowRoot.querySelector('.maplibregl-control-container'); // MapLibre uses same class names
      mapContainer.prepend(navElement);
      navElement.querySelectorAll('button').forEach((btn) => {
        btn.tabIndex = 0;
      });

      // add shortcuts for voice over
      this.shadowRoot.querySelector('.maplibregl-canvas').setAttribute('aria-keyshortcuts', 'ArrowUp ArrowDown ArrowLeft ArrowRight Plus -');

      this.dispatchEvent(new CustomEvent('gsb-map-loaded', {detail: e}));
    });
    this.map.on('error', (e) => {
      this.dispatchEvent(new CustomEvent('gsb-map-error', {detail: e}));
      console.error(e);
    });
    // todo: find better way to resize the map when page loads
    this.map.once('load', () => this.map.resize());

    this.map.on('sourcedata', () => {
      this.dispatchEvent(new CustomEvent('gsb-map-source-data-loaded', {bubbles: true, composed: true}));
    });

    this.dispatchEvent(new CustomEvent('gsb-map-ready', {bubbles: true, composed: true, detail: this.map}));
  }
}

customElements.define(GsbMap.is, GsbMap);
